// Asignación en lote — desvío consciente al spec §3.2 («una sola acción
// por fila»), sancionado por el handoff del panel (decisión 1b): el jefe
// selecciona varias observaciones del proyecto y las manda de una a un
// contratista. Rechazar sigue exigiendo abrir el detalle, porque nadie
// rechaza con un resumen.
//
// Lo que este drawer NO hace es clasificar. La asignación individual ES
// la clasificación de especialidad (ADR-0129) y por eso su modal pide
// especialidad y partida; en el lote la especialidad ya está puesta —
// es el eje por el que el jefe filtró para armar la selección — y
// pedirla otra vez la sobreescribiría en bloque sobre filas que la
// tenían bien.
//
// El enlace público pertenece a una unidad, así que una selección
// multi-unidad emite un enlace POR UNIDAD. Con «Notificar al contratista
// por correo» (default) el servidor le manda los enlaces y el drawer se
// cierra con un toast sólo si salieron TODOS los correos; si el jefe lo
// desmarcó o si el servidor no confirmó alguno, queda la pantalla de
// resultado con los enlaces sin correo para copiarlos (decisión 10 del
// fundador, 2026-10-03). El token crudo sólo viaja en esta respuesta:
// un envío parcial no puede cerrar el drawer, o esos enlaces se pierden.
//
// El plazo es en días CORRIDOS y el copy dice «días», nunca «hábiles».
// El valor por defecto sale de `observationDueDaysFor` (core-engine), la
// misma regla que usa el servidor cuando el body no trae plazo: la tabla
// por severidad de la receta si la declara (`observationDueDays`, facility
// ADR-0134 §6), si no DEFAULT_PLAZO_DIAS_BY_SEVERITY por criticidad; en
// ambos casos la observación más urgente de la selección manda.
//
// Sin taxonomía (ADR-0134): el drawer nunca la leyó —no clasifica—, y la
// especialidad común sólo ordena la nómina cuando la selección la trae.
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import {
  estadoFor,
  estadoLabelsFor,
  observationVisitsEnabled,
  visitContactCoordinationLine,
  type PostventaObservation,
} from "@bitacorapro/api-types";
import { observationDueDaysFor } from "@bitacorapro/core-engine";
import { CopyLinkField } from "./CopyLinkField";
import { Drawer } from "./Drawer";
import { SevPill } from "./SevPill";
import { ProviderListBoundary } from "./skeleton-compositions/ProviderListSkel";
import {
  faltaDeVisita,
  visitaBody,
  VisitaFields,
  visitaErrorMessage,
  VISITA_VACIA,
  type VisitaDraft,
} from "./VisitaFields";
import {
  useAssignFindingBatch,
  useProviders,
  useRecipe,
  useVisitContact,
} from "../lib/api/queries";
import { resolveSeverityCode } from "../lib/postventa/severity";
import { unidadDe } from "../lib/postventa/unit";
import { threadWords } from "../lib/postventa/words";
import { useToast } from "../lib/useToast";

/** Plazos ofrecidos, en días corridos. Incluye los cuatro defaults por
 *  criticidad (3 / 5 / 7 / 10). */
const PLAZOS = [3, 5, 7, 10, 15] as const;

const TOAST_MS = 6000;

function dias(n: number): string {
  return `${n} ${n === 1 ? "día" : "días"}`;
}

/** Las etiquetas de estado salen del módulo compartido (fitness
 *  postventa-labels-single-source); el plural es la etiqueta + «s». */
function estadoPl(label: string, n: number): string {
  return n === 1 ? label : `${label}s`;
}

/** «… 1 quedó Aceptada.» / «… 3 quedaron Aceptadas.» */
function quedaronAceptadas(k: number, aceptada: string): string {
  return `${k} ${k === 1 ? "quedó" : "quedaron"} ${estadoPl(aceptada, k)}.`;
}

type EmittedLink = {
  assetId: string;
  assetLabel: string;
  url: string;
  findingIds: string[];
  notified?: boolean | undefined;
};

export function AssignBatchDrawer({
  observations,
  especialidadLabel,
  onClose,
  onDone,
}: {
  /** Las filas seleccionadas, ya filtradas a las asignables por quien
   *  abre el drawer. */
  observations: PostventaObservation[];
  /** El nombre de la especialidad común, si la selección comparte una.
   *  Ordena la nómina; no se persiste. */
  especialidadLabel: string | null;
  onClose: () => void;
  /** La asignación terminó (con toast o tras «Listo» en la pantalla de
   *  enlaces): quien abrió el drawer limpia la selección y lo cierra. */
  onDone: () => void;
}) {
  const { data: recipe } = useRecipe();
  const words = threadWords(recipe);
  // Las etiquetas de estado de ESTA receta (el toast de la aceptación
  // implícita): «Solicitada» → «Aceptada» en postventa.
  const label = (kind: "open" | "accepted") =>
    estadoFor(kind, recipe ? estadoLabelsFor(recipe) : undefined).label;
  const [providerId, setProviderId] = useState("");
  const [note, setNote] = useState("");
  const [notificar, setNotificar] = useState(true);
  // F8 — la misma visita (opcional) para todo el lote, sólo si la receta
  // agenda (contraparte externa).
  const visitas = observationVisitsEnabled(recipe);
  const [visita, setVisita] = useState<VisitaDraft>(VISITA_VACIA);
  const faltaVisita = visitas ? faltaDeVisita(visita, { optional: true }) : null;
  // La observación más urgente de la selección fija el default.
  const plazoDefault = useMemo(
    () =>
      observationDueDaysFor(
        recipe ?? {},
        observations.map((o) => ({
          severity: o.severity,
          effectiveSeverity: resolveSeverityCode(o),
        }))
      ),
    [recipe, observations]
  );
  // `null` = el jefe no lo tocó: manda el default (que puede llegar
  // después que la receta). Si lo cambia, manda lo que eligió.
  const [plazoElegido, setPlazoDias] = useState<number | null>(null);
  const plazoDias = plazoElegido ?? plazoDefault;
  const plazos = useMemo(
    () => [...new Set([...PLAZOS, plazoDefault])].sort((a, b) => a - b),
    [plazoDefault]
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [emitted, setEmitted] = useState<{
    links: EmittedLink[];
    implicitlyAccepted: number;
    providerShortName: string;
  } | null>(null);

  const batch = useAssignFindingBatch();
  const providers = useProviders();
  const toast = useToast();
  const listRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const listLabelId = useId();
  const noteId = useId();
  const plazoId = useId();
  const plazoHintId = useId();
  const asignarHintId = useId();
  const notifyId = useId();

  const { sugeridos, resto } = useMemo(() => {
    const list = providers.data?.providers ?? [];
    if (!especialidadLabel) return { sugeridos: [], resto: list };
    const calza = (p: (typeof list)[number]) =>
      (p.specialty ?? "").toLowerCase().includes(especialidadLabel.toLowerCase());
    return { sugeridos: list.filter(calza), resto: list.filter((p) => !calza(p)) };
  }, [providers.data, especialidadLabel]);
  const nomina = [...sugeridos, ...resto];

  // La pantalla de resultado reemplaza el formulario: el foco va a su
  // título (el h2 del drawer) para que el lector de pantalla lo lea una
  // vez, sin un anuncio adicional.
  useEffect(() => {
    if (!emitted) return;
    const h = resultRef.current?.closest(".drawer")?.querySelector<HTMLElement>(".drawer__title");
    if (h) {
      h.tabIndex = -1;
      h.focus();
    }
  }, [emitted]);

  // Roving tabindex over the radios, same as GestionPanel's assign list: the
  // checked option (or the first when nothing is chosen) takes Tab; arrows
  // move both the selection and the focus, as a native radio group would.
  function onListKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (nomina.length === 0) return;
    const delta =
      e.key === "ArrowDown" || e.key === "ArrowRight"
        ? 1
        : e.key === "ArrowUp" || e.key === "ArrowLeft"
          ? -1
          : 0;
    if (delta === 0) return;
    e.preventDefault();
    const i = nomina.findIndex((p) => p.id === providerId);
    const next = nomina[(Math.max(i, 0) + delta + nomina.length) % nomina.length]!;
    setProviderId(next.id);
    listRef.current?.querySelector<HTMLElement>(`[data-provider-id="${next.id}"]`)?.focus();
  }

  async function submit() {
    // «Asignar» va con aria-disabled (sigue siendo alcanzable y lee su
    // motivo); el bloqueo real es éste.
    if (!providerId || batch.isPending || faltaVisita !== null) return;
    setSubmitError(null);
    try {
      const res = await batch.mutateAsync({
        findingIds: observations.map((o) => o.id),
        providerId,
        plazoDias,
        notificar,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(visitas && visitaBody(visita) ? { visit: visitaBody(visita) } : {}),
      });
      const n = observations.length;
      // Toast sólo si salieron TODAS las unidades: `contractorNotified`
      // es todo-o-nada en el servidor, y un enlace con `notified: false`
      // basta para quedarse en la pantalla de enlaces.
      const todasEnviadas =
        notificar &&
        res.contractorNotified !== false &&
        res.links.every((l) => l.notified !== false);
      if (todasEnviadas) {
        const plazo = res.plazoDias ?? plazoDias;
        const k = res.implicitlyAccepted;
        toast.show(
          `${n} ${n === 1 ? "observación asignada" : "observaciones asignadas"} a ${res.providerShortName}. Vence en ${dias(plazo)}.${k > 0 ? ` ${quedaronAceptadas(k, label("accepted"))}` : ""}`,
          "ok",
          { durationMs: TOAST_MS }
        );
        onDone();
        return;
      }
      setEmitted({
        links: res.links,
        implicitlyAccepted: res.implicitlyAccepted,
        providerShortName: res.providerShortName,
      });
    } catch (err) {
      setSubmitError(visitaErrorMessage(err, words, "No pudimos asignar las observaciones."));
    }
  }

  const n = observations.length;

  return (
    <Drawer
      title={
        emitted
          ? "Observaciones asignadas"
          : n === 1
            ? "Asignar 1 observación"
            : `Asignar ${n} observaciones`
      }
      onClose={emitted ? onDone : onClose}
      lead={
        emitted ? (
          <p className="drawer__lead-title">
            {emitted.links.length === 1
              ? `Un enlace para ${emitted.providerShortName}.`
              : `${emitted.links.length} enlaces para ${emitted.providerShortName}, uno por ${words.assetLower}.`}{" "}
            Cada uno muestra sólo las observaciones de esa {words.assetLower}.
          </p>
        ) : (
          <ul className="as-list" aria-label="Observaciones seleccionadas">
            {observations.map((o) => (
              <li key={o.id}>
                <span className="drawer__lead-title">
                  {o.aiTitle ?? o.humanDescription ?? o.aiDescription ?? "—"}
                </span>
                <span className="drawer__lead-chips">
                  <span>
                    {words.asset} {o.assetUnit ?? unidadDe(o.assetLabel)}
                  </span>
                  <span aria-hidden="true">·</span>
                  <span>{o.recintoNombre ?? o.areaName}</span>
                  {o.partidaNombre && (
                    <>
                      <span aria-hidden="true">·</span>
                      <span>{o.partidaNombre}</span>
                    </>
                  )}
                  <SevPill code={resolveSeverityCode(o)} hideCode />
                </span>
              </li>
            ))}
          </ul>
        )
      }
      footer={
        emitted ? (
          <button type="button" className="btn btn--primary" onClick={onDone}>
            Listo
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
              disabled={batch.isPending}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void submit()}
              aria-disabled={!providerId || batch.isPending || faltaVisita !== null}
              aria-describedby={!providerId || faltaVisita ? asignarHintId : undefined}
            >
              {batch.isPending ? "Asignando…" : "Asignar"}
            </button>
            {(!providerId || faltaVisita) && (
              <span id={asignarHintId} className="sr-only">
                {!providerId ? `Elige un ${words.assigneeLower} para continuar` : faltaVisita}
              </span>
            )}
          </>
        )
      }
    >
      {emitted ? (
        <div ref={resultRef}>
          <p className="cell-sub">No se envió correo: copia y comparte los enlaces.</p>
          {emitted.implicitlyAccepted > 0 && (
            // El jefe tiene que saber que aceptó, no sólo que asignó:
            // aceptar es una decisión sobre la observación del
            // propietario y queda como hito en su hilo.
            <p className="cell-sub drawer__note">
              {emitted.implicitlyAccepted === 1
                ? `Una de ellas estaba ${label("open")} y quedó ${label("accepted")} al asignarla.`
                : `${emitted.implicitlyAccepted} estaban ${estadoPl(label("open"), 2)} y quedaron ${estadoPl(label("accepted"), 2)} al asignarlas.`}
            </p>
          )}
          {emitted.links.some((l) => l.notified === true) && (
            // Envío parcial: las unidades cuyo correo salió se listan, sin
            // enlace que copiar (el contratista ya lo tiene).
            <ul className="as-list" aria-label="Enviado por correo">
              {emitted.links
                .filter((l) => l.notified === true)
                .map((l) => (
                  <li key={l.assetId} className="cell-sub">
                    {l.assetLabel} · Enviado por correo
                  </li>
                ))}
            </ul>
          )}
          {emitted.links
            .filter((l) => l.notified !== true)
            .map((l) => (
              <CopyLinkField
                key={l.assetId}
                id={`assign-batch-url-${l.assetId}`}
                label={`${l.assetLabel} · ${l.findingIds.length} ${
                  l.findingIds.length === 1 ? "observación" : "observaciones"
                }`}
                value={l.url}
              />
            ))}
        </div>
      ) : (
        <>
          <div className="field">
            <div className="field__label" id={listLabelId}>
              {words.assignee}
            </div>
            <ProviderListBoundary query={providers} metaClassName="cell-meta">
              {providers.data && providers.data.providers.length === 0 && (
                <p className="cell-sub">
                  Todavía no hay {words.assigneePluralLower} en la nómina. Sin nómina no hay a quién
                  asignarle las observaciones: cárgalos primero desde {words.assigneePlural}.
                </p>
              )}
              {sugeridos.length > 0 && (
                <p className="cell-sub">Sugeridos por tipo de reparación · {especialidadLabel}</p>
              )}
              <div
                ref={listRef}
                className="prov-list"
                role="radiogroup"
                aria-labelledby={listLabelId}
                onKeyDown={onListKeyDown}
              >
                {nomina.map((p, i) => {
                  const checked = providerId === p.id;
                  const focusable = checked || (providerId === "" && i === 0);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      tabIndex={focusable ? 0 : -1}
                      data-provider-id={p.id}
                      className={`prov-option${checked ? " prov-option--selected" : ""}`}
                      onClick={() => setProviderId(p.id)}
                    >
                      <span className="prov-option__meta">
                        <span className="prov-option__name">{p.shortName}</span>
                        <span className="cell-meta" style={{ display: "block", marginTop: 2 }}>
                          {[
                            p.specialty,
                            p.activeJobs === 0 ? "sin trabajos activos" : `${p.activeJobs} activos`,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                      {checked && (
                        <span className="prov-option__check" aria-hidden="true">
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </ProviderListBoundary>
          </div>

          <div className="field">
            <label className="field__label" htmlFor={plazoId}>
              Plazo
            </label>
            <select
              id={plazoId}
              className="select"
              value={plazoDias}
              onChange={(e) => setPlazoDias(Number(e.target.value))}
              aria-describedby={plazoHintId}
            >
              {plazos.map((d) => (
                <option key={d} value={d}>
                  {dias(d)}
                </option>
              ))}
            </select>
            <p id={plazoHintId} className="field__hint">
              Por defecto {dias(plazoDefault)} según la criticidad más alta
            </p>
          </div>

          <div className="field">
            <label className="field__label" htmlFor={noteId}>
              Instrucciones para el {words.assigneeLower} (opcional)
            </label>
            <textarea
              id={noteId}
              className="textarea"
              rows={3}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={`Se envían al ${words.assigneeLower} junto con el enlace`}
            />
          </div>

          {visitas && (
            <>
              {/* ADR-0150 — con quién coordinar el acceso, por unidad. */}
              {[...new Map(observations.map((o) => [o.assetId, o])).values()].map((o) => (
                <AccesoUnidad
                  key={o.assetId}
                  assetId={o.assetId}
                  unidad={`${words.asset} ${o.assetUnit ?? unidadDe(o.assetLabel)}`}
                  multiple={new Set(observations.map((x) => x.assetId)).size > 1}
                />
              ))}
              <VisitaFields
                value={visita}
                onChange={setVisita}
                optional
                elAssetParty={words.elAssetParty}
                disabled={batch.isPending}
              />
              {faltaVisita && <p className="field__hint">{faltaVisita}</p>}
            </>
          )}

          <label className="drawer__notify" htmlFor={notifyId}>
            <input
              id={notifyId}
              type="checkbox"
              checked={notificar}
              onChange={(e) => setNotificar(e.target.checked)}
            />
            Notificar al {words.assigneeLower} por correo
          </label>

          {submitError && (
            <p className="auth-error" role="alert">
              {submitError}
            </p>
          )}
        </>
      )}
    </Drawer>
  );
}

/** ADR-0150 — «Arrendada · coordinar con {nombre} {teléfono}» de UNA
 *  unidad de la selección; nada si no tiene contacto vigente (o mientras
 *  carga, o si la lectura falla: la línea es ayuda, no un requisito). */
function AccesoUnidad({
  assetId,
  unidad,
  multiple,
}: {
  assetId: string;
  unidad: string;
  multiple: boolean;
}) {
  const { data } = useVisitContact(assetId);
  const c = data?.visitContact ?? null;
  if (!c) return null;
  const linea = visitContactCoordinationLine(c);
  return <p className="field__hint">{multiple ? `${unidad}: ${linea}` : linea}</p>;
}
